/**
 * PreMeetingSetupPanel — sessietemplate & blokken (modal hub of embedded in countdown)
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  CloseIcon,
  DeleteIcon,
  VideoIcon,
  CommunityIcon,
} from '../../icons';
import { AddIcon, SaveDiskIcon } from '../../icons';
import {
  useStructuredGrowth,
  generateTemplate,
  PRESET_TEMPLATES,
  loadCustomTemplates,
  saveCustomTemplate,
  deleteCustomTemplate,
  BlockEditorRow,
} from '../../../modules/structuredGrowth';
import type { SessionBlock, CustomTemplate, BlockSlide, BlockMediaType } from '../../../modules/structuredGrowth';

// ── BlockMediaEditor ───────────────────────────────────────────────────────

const FONT_FAMILIES = [
  { value: 'sans', label: 'Modern (Sans)' },
  { value: 'serif', label: 'Klassiek (Serif)' },
  { value: 'mono', label: 'Technisch (Mono)' },
] as const;

const FONT_SIZES = [
  { value: 'sm', label: 'Klein' },
  { value: 'md', label: 'Normaal' },
  { value: 'lg', label: 'Groot' },
  { value: 'xl', label: 'Extra groot' },
] as const;

const ANIMATIONS = [
  { value: 'none', label: 'Geen' },
  { value: 'fade', label: 'Fade' },
  { value: 'kenburns', label: 'Ken Burns (zoom)' },
  { value: 'slide', label: 'Slide' },
] as const;

const COUNTDOWN_OPTIONS = [5, 10, 15, 20, 30];

interface BlockMediaEditorProps {
  block: SessionBlock;
  onChange: (updated: Partial<SessionBlock>) => void;
}

const BlockMediaEditor: React.FC<BlockMediaEditorProps> = ({ block, onChange }) => {
  const mediaType: BlockMediaType = block.mediaType ?? 'none';
  const slides: BlockSlide[] = block.slides ?? [];

  const setMediaType = (t: BlockMediaType) => onChange({ mediaType: t });

  const addSlide = () => {
    if (slides.length >= 10) return;
    onChange({
      slides: [...slides, {
        id: `slide-${Date.now()}`,
        imageUrl: '',
        caption: '',
        fontFamily: 'sans',
        fontSize: 'md',
        animation: 'fade',
      }],
    });
  };

  const updateSlide = (idx: number, patch: Partial<BlockSlide>) => {
    const next = slides.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange({ slides: next });
  };

  const removeSlide = (idx: number) => {
    onChange({ slides: slides.filter((_, i) => i !== idx) });
  };

  return (
    <div className="mt-1.5 mb-2 pl-6 pr-2 space-y-3 border-l-2 border-sage-100">
      <div>
        <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Countdown maximum</p>
        <div className="flex gap-1 flex-wrap">
          {COUNTDOWN_OPTIONS.map((min) => (
            <button
              key={min}
              type="button"
              onClick={() => onChange({ countdownMaxMinutes: min })}
              className={`h-6 px-2.5 rounded-full text-[10px] font-semibold transition-colors ${
                (block.countdownMaxMinutes ?? block.durationMinutes) === min
                  ? 'bg-sage-400 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {min} min
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange({ countdownMaxMinutes: undefined })}
            className={`h-6 px-2.5 rounded-full text-[10px] font-semibold transition-colors ${
              block.countdownMaxMinutes === undefined ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
          >
            Auto
          </button>
        </div>
      </div>

      <div>
        <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Media achtergrond</p>
        <div className="flex gap-1">
          {(['none', 'youtube', 'slides'] as BlockMediaType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setMediaType(t)}
              className={`h-6 px-2.5 rounded-full text-[10px] font-semibold transition-colors ${
                mediaType === t ? 'bg-sage-400 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {t === 'none' ? 'Geen' : t === 'youtube' ? 'YouTube' : 'Foto slides'}
            </button>
          ))}
        </div>
      </div>

      {mediaType === 'youtube' && (
        <div>
          <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1">YouTube URL</p>
          <input
            type="url"
            value={block.youtubeUrl ?? ''}
            onChange={(e) => onChange({ youtubeUrl: e.target.value })}
            placeholder="https://youtube.com/watch?v=..."
            className="w-full h-7 text-[10px] px-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-sage-300"
          />
        </div>
      )}

      {mediaType === 'slides' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">
              Foto slides ({slides.length}/10)
            </p>
            <label className="flex items-center gap-1 text-[9px] text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={block.slideReplay ?? false}
                onChange={(e) => onChange({ slideReplay: e.target.checked })}
                className="w-3 h-3 rounded"
              />
              Herhalen
            </label>
          </div>

          {slides.map((slide, idx) => (
            <div key={slide.id} className="bg-gray-50 rounded-xl p-2.5 space-y-2 border border-gray-100">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-bold text-gray-300 w-4">{idx + 1}</span>
                <input
                  type="url"
                  value={slide.imageUrl}
                  onChange={(e) => updateSlide(idx, { imageUrl: e.target.value })}
                  placeholder="Afbeelding URL of Firebase link..."
                  className="flex-1 h-6 text-[10px] px-2 rounded-lg border border-gray-200 focus:outline-none focus:border-sage-300 bg-white"
                />
                <button
                  type="button"
                  onClick={() => removeSlide(idx)}
                  className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
                >
                  <DeleteIcon size={10} weight="regular" />
                </button>
              </div>

              {slide.imageUrl && (
                <div className="h-14 rounded-lg overflow-hidden bg-gray-200">
                  <img src={slide.imageUrl} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              )}

              <input
                type="text"
                value={slide.caption ?? ''}
                onChange={(e) => updateSlide(idx, { caption: e.target.value })}
                placeholder="Bijschrift of tekst op slide..."
                className="w-full h-6 text-[10px] px-2 rounded-lg border border-gray-200 focus:outline-none focus:border-sage-300 bg-white"
              />

              <div className="flex gap-1.5 flex-wrap">
                <select
                  value={slide.fontFamily ?? 'sans'}
                  onChange={(e) => updateSlide(idx, { fontFamily: e.target.value as BlockSlide['fontFamily'] })}
                  className="h-6 text-[9px] px-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none"
                >
                  {FONT_FAMILIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select
                  value={slide.fontSize ?? 'md'}
                  onChange={(e) => updateSlide(idx, { fontSize: e.target.value as BlockSlide['fontSize'] })}
                  className="h-6 text-[9px] px-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none"
                >
                  {FONT_SIZES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select
                  value={slide.animation ?? 'fade'}
                  onChange={(e) => updateSlide(idx, { animation: e.target.value as BlockSlide['animation'] })}
                  className="h-6 text-[9px] px-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none"
                >
                  {ANIMATIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={slide.displaySeconds ?? ''}
                    onChange={(e) => updateSlide(idx, { displaySeconds: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                    placeholder="Auto"
                    min={1}
                    max={120}
                    className="w-10 h-6 text-[9px] px-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none text-right"
                  />
                  <span className="text-[9px] text-gray-400">sec</span>
                </div>
              </div>
            </div>
          ))}

          {slides.length < 10 && (
            <button
              type="button"
              onClick={addSlide}
              className="w-full h-7 text-[10px] font-medium rounded-lg border border-dashed border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors flex items-center justify-center gap-1"
            >
              <AddIcon size={10} weight="bold" />
              Slide toevoegen ({10 - slides.length} over)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── PreMeetingSetupPanel ───────────────────────────────────────────────────

export interface PreMeetingSetupPanelProps {
  entityId: string;
  entityType: 'mastermind' | 'community' | 'networkclub';
  userId: string;
  memberCount: number;
  memberNames: string[];
  memberAvatars?: (string | null)[];
  /** `modal` = volledig scherm (hub); `embedded` = tab in countdown */
  variant?: 'modal' | 'embedded';
  /** Alleen bij variant modal: na opslaan meeting starten */
  onStart?: () => void;
  onClose?: () => void;
  /** Alleen bij variant embedded: na succesvol opslaan */
  onSaved?: () => void;
}

const PreMeetingSetupPanel: React.FC<PreMeetingSetupPanelProps> = ({
  entityId,
  entityType,
  userId,
  memberCount,
  memberNames,
  memberAvatars = [],
  variant = 'modal',
  onStart,
  onClose,
  onSaved,
}) => {
  const { settings, updateSettings } = useStructuredGrowth(entityId, entityType);
  const currentBlocks = settings?.sessionStructure ?? [];
  const [draft, setDraft] = useState<SessionBlock[]>([]);
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [saving, setSaving] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [expandedBlockIds, setExpandedBlockIds] = useState<Set<string>>(new Set());
  const loaded = useRef(false);
  const effectiveMembers = Math.max(1, memberCount);

  const toggleBlockExpand = (id: string) =>
    setExpandedBlockIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleBlockMediaChange = (blockId: string, patch: Partial<SessionBlock>) =>
    setDraft((prev) => prev.map((b) => (b.id === blockId ? { ...b, ...patch } : b)));

  useEffect(() => {
    if (currentBlocks.length > 0 && draft.length === 0) {
      setDraft([...currentBlocks].sort((a, b) => a.order - b.order));
    } else if (currentBlocks.length === 0 && draft.length === 0) {
      setDraft(generateTemplate(effectiveMembers));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBlocks.length]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void loadCustomTemplates(entityId).then(setCustomTemplates);
  }, [entityId]);

  const applyTemplate = (blocks: SessionBlock[]) => {
    setDraft(blocks.map((b, i) => ({ ...b, order: i })));
  };

  const totalMinutes = draft.reduce(
    (sum, b) => sum + (b.perPerson && effectiveMembers > 1 ? b.durationMinutes * effectiveMembers : b.durationMinutes),
    0,
  );

  const persistStructure = async (): Promise<boolean> => {
    if (!userId) return false;
    setSaving(true);
    try {
      await updateSettings({ sessionStructure: draft }, userId);
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleModalStart = async () => {
    const ok = await persistStructure();
    if (ok && onStart) onStart();
  };

  const handleEmbeddedSave = async () => {
    const ok = await persistStructure();
    if (ok) onSaved?.();
  };

  const handleLabelChange = (id: string, v: string) =>
    setDraft((prev) => prev.map((b) => (b.id === id ? { ...b, label: v } : b)));
  const handleDurationChange = (id: string, v: number) =>
    setDraft((prev) => prev.map((b) => (b.id === id ? { ...b, durationMinutes: v } : b)));
  const handleTogglePP = (id: string) =>
    setDraft((prev) => prev.map((b) => (b.id === id ? { ...b, perPerson: !b.perPerson } : b)));
  const handleMoveUp = (idx: number) => {
    if (idx <= 0) return;
    setDraft((prev) => {
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr.map((b, i) => ({ ...b, order: i }));
    });
  };
  const handleMoveDown = (idx: number) => {
    setDraft((prev) => {
      if (idx >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr.map((b, i) => ({ ...b, order: i }));
    });
  };
  const handleDeleteBlock = (id: string) =>
    setDraft((prev) => prev.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })));
  const handleAddBlock = () =>
    setDraft((prev) => [
      ...prev,
      {
        id: `blk-${Date.now()}`,
        type: 'custom',
        label: 'Nieuw blok',
        durationMinutes: 5,
        order: prev.length,
      },
    ]);
  const handleAssignedChange = (id: string, indices: number[]) =>
    setDraft((prev) =>
      prev.map((b) =>
        b.id === id ? { ...b, assignedMembers: indices, memberSlotCount: indices.length || effectiveMembers } : b,
      ),
    );

  const handleSaveTemplate = async () => {
    if (!saveName.trim()) return;
    setSavingTemplate(true);
    await saveCustomTemplate(entityId, saveName.trim(), draft);
    const updated = await loadCustomTemplates(entityId);
    setCustomTemplates(updated);
    setSaveName('');
    setShowSaveInput(false);
    setSavingTemplate(false);
  };

  const handleDeleteTemplate = async (tplId: string) => {
    await deleteCustomTemplate(entityId, tplId);
    setCustomTemplates((prev) => prev.filter((t) => t.id !== tplId));
  };

  const body = (
    <>
      {variant === 'embedded' && (
        <div className="px-4 pt-3 pb-2 border-b border-gray-100 shrink-0 bg-white">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Sessietemplate</p>
          <p className="text-[13px] font-semibold text-gray-900 mt-0.5">Blokken &amp; tijden</p>
          <p className="text-[10px] text-gray-500 mt-1 leading-snug">
            Standaard templates, eigen templates en media per blok.
          </p>
        </div>
      )}

      {variant === 'modal' && (
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Meeting instellen</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5">Sessietemplate bewerken</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-300 hover:text-gray-500 transition-colors">
            <CloseIcon size={16} weight="bold" />
          </button>
        </div>
      )}

      <div
        className={`space-y-4 ${
          variant === 'embedded'
            ? 'px-4 py-3'
            : 'overflow-y-auto flex-1 min-h-0 px-5 py-4'
        }`}
      >
        <div>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Standaard templates</p>
          <div className="grid grid-cols-3 gap-1.5">
            {PRESET_TEMPLATES.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => applyTemplate(generateTemplate(t.memberCount))}
                className="h-8 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-sage-300 hover:bg-sage-50 transition-colors"
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => applyTemplate(generateTemplate(effectiveMembers))}
            className="w-full h-8 mt-1.5 text-[11px] font-medium rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
          >
            Automatisch ({effectiveMembers} leden, 60 min)
          </button>
        </div>

        {customTemplates.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Eigen templates</p>
            <div className="space-y-1">
              {customTemplates.map((tpl) => (
                <div key={tpl.id} className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => applyTemplate(tpl.blocks)}
                    className="flex-1 h-8 text-[11px] font-medium text-left px-3 rounded-lg border border-gray-200 text-gray-600 hover:border-sage-300 hover:bg-sage-50 transition-colors truncate"
                  >
                    {tpl.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteTemplate(tpl.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-300 hover:text-red-500 hover:border-red-200 transition-colors shrink-0"
                    title="Verwijderen"
                  >
                    <DeleteIcon size={11} weight="regular" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Sessieblokken bewerken</p>
            <span className="text-[10px] text-gray-500 font-medium">{totalMinutes} min totaal</span>
          </div>
          <div className="space-y-1">
            {draft.map((block, idx) => {
              const isExpanded = expandedBlockIds.has(block.id);
              const hasMedia = block.mediaType && block.mediaType !== 'none';
              return (
                <div key={block.id}>
                  <BlockEditorRow
                    block={block}
                    idx={idx}
                    total={draft.length}
                    effectiveMembers={effectiveMembers}
                    onLabelChange={handleLabelChange}
                    onDurationChange={handleDurationChange}
                    onTogglePP={handleTogglePP}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onDelete={handleDeleteBlock}
                    memberNames={memberNames}
                    memberAvatars={memberAvatars}
                    onAssignedChange={handleAssignedChange}
                  />
                  <div className="flex justify-end px-2 -mt-1 mb-1">
                    <button
                      type="button"
                      onClick={() => toggleBlockExpand(block.id)}
                      title={isExpanded ? 'Media sluiten' : 'Countdown & media instellen'}
                      className={`text-[9px] flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
                        isExpanded ? 'bg-sage-100 text-sage-600' : hasMedia ? 'text-sage-500 hover:text-sage-700' : 'text-gray-300 hover:text-gray-500'
                      }`}
                    >
                      {hasMedia && <span className="w-1.5 h-1.5 rounded-full bg-sage-400" />}
                      {isExpanded ? 'Sluiten' : 'Media'}
                      <svg className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {isExpanded && (
                    <BlockMediaEditor block={block} onChange={(patch) => handleBlockMediaChange(block.id, patch)} />
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={handleAddBlock}
            className="w-full h-7 mt-1.5 text-[10px] font-medium rounded-lg border border-dashed border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors flex items-center justify-center gap-1"
          >
            <AddIcon size={10} weight="bold" />
            Blok toevoegen
          </button>
        </div>

        <div className="border-t border-gray-100 pt-3">
          {showSaveInput ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleSaveTemplate()}
                placeholder="Naam van template..."
                className="flex-1 h-8 text-[11px] px-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-sage-300"
              />
              <button type="button" onClick={() => void handleSaveTemplate()} disabled={!saveName.trim() || savingTemplate}
                className="h-8 px-3 text-[11px] font-semibold text-white bg-sage-400 rounded-lg hover:bg-sage-500 disabled:opacity-50 transition-colors">
                {savingTemplate ? '...' : 'Opslaan'}
              </button>
              <button
                type="button"
                onClick={() => { setShowSaveInput(false); setSaveName(''); }}
                className="h-8 px-2 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                Annuleer
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowSaveInput(true)}
              className="w-full h-8 text-[11px] font-medium rounded-lg border border-dashed border-sage-200 text-sage-600 hover:bg-sage-50 transition-colors flex items-center justify-center gap-1.5"
            >
              <SaveDiskIcon size={11} weight="regular" />
              Huidige sessie opslaan als template
            </button>
          )}
        </div>

        {memberNames.length > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-3">
            <CommunityIcon size={11} weight="regular" />
            <span>
              {memberNames.length} {memberNames.length === 1 ? 'lid' : 'leden'}
            </span>
            <span className="text-gray-300 mx-0.5">&middot;</span>
            <span className="truncate">
              {memberNames.slice(0, 5).join(', ')}
              {memberNames.length > 5 ? ` +${memberNames.length - 5}` : ''}
            </span>
          </div>
        )}
      </div>

      <div className={`px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2 shrink-0 ${variant === 'modal' ? 'px-5' : ''}`}>
        {variant === 'modal' ? (
          <>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-9 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => void handleModalStart()}
              disabled={saving || !userId}
              className="flex-1 h-9 text-xs font-semibold text-white bg-sage-400 rounded-xl hover:bg-sage-500 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
            >
              <VideoIcon size={13} weight="regular" />
              {saving ? 'Opslaan...' : 'Start Meeting'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => void handleEmbeddedSave()}
            disabled={saving || !userId}
            className="w-full h-9 text-xs font-semibold text-white bg-sage-500 rounded-xl hover:bg-sage-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            <SaveDiskIcon size={13} weight="regular" />
            {saving ? 'Opslaan...' : 'Sessie opslaan'}
          </button>
        )}
      </div>
    </>
  );

  if (variant === 'embedded') {
    return (
      <div className="flex flex-col w-full rounded-2xl border border-gray-200/80 bg-white text-gray-900 shadow-lg overflow-hidden">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] w-full max-w-[540px] flex flex-col">
        {body}
      </div>
    </div>
  );
};

export default PreMeetingSetupPanel;
